import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { NumberGenerator } from "@/components/NumberGenerator";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Copy, Zap } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import numbersToolsHero from "@/assets/numbers-tools-hero.jpg";

const DecimalGenerator = () => {
  const [decimalPlaces, setDecimalPlaces] = useState("2");
  const [count, setCount] = useState("10");
  const [results, setResults] = useState<string[]>([]);

  const generate = () => {
    const places = parseInt(decimalPlaces);
    const num = parseInt(count);
    
    if (isNaN(places) || isNaN(num) || places < 1 || places > 10 || num < 1 || num > 1000) {
      toast.error("Invalid input");
      return;
    }

    const array = new Uint32Array(num);
    crypto.getRandomValues(array);
    const decimals = Array.from(array).map(
      (val) => (val / 0xffffffff).toFixed(places)
    );
    
    setResults(decimals);
    toast.success(`Generated ${num} random decimals`);
  };

  const copy = () => {
    navigator.clipboard.writeText(results.join("\n"));
    toast.success("Copied to clipboard");
  };

  return (
    <Card className="p-6 shadow-[var(--shadow-medium)]">
      <div className="flex items-center gap-2 mb-6">
        <Zap className="w-6 h-6 text-accent" />
        <h2 className="text-2xl font-bold">Decimal Fractions</h2>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <Label htmlFor="places">Decimal Places (1-10)</Label>
          <Input
            id="places"
            type="number"
            value={decimalPlaces}
            onChange={(e) => setDecimalPlaces(e.target.value)}
            min="1"
            max="10"
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="count">Count (1-1000)</Label>
          <Input
            id="count"
            type="number"
            value={count}
            onChange={(e) => setCount(e.target.value)}
            min="1"
            max="1000"
            className="mt-1"
          />
        </div>
      </div>

      <Button onClick={generate} className="w-full mb-4">
        Generate Decimals
      </Button>

      {results.length > 0 && (
        <div className="bg-secondary rounded-lg p-4">
          <div className="flex justify-between items-center mb-2">
            <p className="text-sm text-muted-foreground">Results:</p>
            <Button variant="ghost" size="sm" onClick={copy}>
              <Copy className="w-4 h-4 mr-1" />
              Copy
            </Button>
          </div>
          <div className="text-sm font-mono text-foreground max-h-64 overflow-y-auto">
            {results.join(", ")}
          </div>
        </div>
      )}
    </Card>
  );
};

const Numbers = () => {
  return (
    <Layout>
      <SEO 
        title="RandomGen Numbers Generator - Free Integer & Decimal Random Number Tool"
        description="Generate random integers and decimal fractions with our cryptographically secure random number generator. Perfect for statistics, probability, games, and lottery numbers. Free random number tool."
        canonical="https://numbers-generator.com/numbers"
        keywords="random integers, decimal generator, number sequences, random numbers, statistics, probability calculator, free random generator"
      />
      <div className="container mx-auto px-4 py-12">
        <header className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            RandomGen Numbers Generator - Integer & Decimal
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Generate random integers, decimal fractions, and number sequences with our cryptographically secure 
            random number generator. Perfect for statistics, games, and lottery numbers.
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl mx-auto">
          <div className="lg:col-span-2">
            <NumberGenerator />
          </div>
          <div className="lg:col-span-2">
            <DecimalGenerator />
          </div>
        </div>

        {/* Hero Image at Bottom */}
        <div className="mt-12 rounded-2xl overflow-hidden shadow-2xl max-w-5xl mx-auto">
          <img 
            src={numbersToolsHero} 
            alt="Professional random number generator technology showing binary code, mathematical formulas, and digital displays for integer and decimal generation"
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>
    </Layout>
  );
};

export default Numbers;